import { lazy, Suspense, useMemo } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import {
  ArrowDownRight,
  ArrowUpRight,
  Box,
  ChevronRight,
  MessageCircle,
  Phone,
  Receipt,
  Truck,
  Users,
  Wallet,
} from 'lucide-react'
import { useDatos } from '../store/datos'
import { useSesion } from '../store/sesion'
import { useUI } from '../store/ui'
import { Pagina } from '../components/Layout'
import { Avatar, Boton, Tarjeta } from '../components/ui/base'
import { BotonInstalarApp } from '../components/InstalarApp'
import { cn } from '../lib/cn'
import {
  diasHasta,
  fecha,
  fechaLarga,
  hora,
  inicioDia,
  inicioMes,
  inicioSemana,
  money,
  moneyCorto,
  num,
  pct,
  relativo,
} from '../lib/format'
import {
  desde,
  resumenInventario,
  seguimientosPendientes,
  serieDiaria,
  suma,
  variacion,
  vigentes,
} from '../lib/metricas'
import { ESTILO_WHATSAPP } from '../lib/tonos'

// Los mismos "neón" que se usan en Inventario para poco stock: bordes
// que se notan de verdad, no un tono pastel más.
const BORDE_ORO_NEON = {
  borderColor: '#e8b400',
  borderWidth: 2,
  boxShadow: '0 0 0 1px rgba(232,180,0,0.35), 0 0 16px 2px rgba(232,180,0,0.6)',
}
const BORDE_ROJO_NEON = {
  borderColor: '#ff1a3c',
  borderWidth: 2,
  boxShadow: '0 0 0 1px rgba(255,26,60,0.35), 0 0 16px 2px rgba(255,26,60,0.65)',
}

const GraficoVentas = lazy(() => import('../components/GraficoVentas'))

export function Inicio() {
  const datos = useDatos()
  const marcarPagada = useDatos((s) => s.marcarPagada)
  const usuario = useSesion((s) => s.usuario)!
  const avisar = useUI((s) => s.avisar)
  const navegar = useNavigate()
  const esAdmin = usuario.rol === 'admin'

  // Un vendedor ve su propio desempeño; el administrador ve el del negocio.
  const ventas = useMemo(
    () => (esAdmin ? datos.ventas : datos.ventas.filter((v) => v.usuarioId === usuario.id)),
    [datos.ventas, esAdmin, usuario.id],
  )

  const inv = useMemo(
    () => resumenInventario(datos.productos, datos.ajustes.stockMinimoGlobal),
    [datos.productos, datos.ajustes.stockMinimoGlobal],
  )

  const hoy = useMemo(() => variacion(ventas, inicioDia()), [ventas])
  const semana = useMemo(() => variacion(ventas, inicioSemana()), [ventas])
  const mes = useMemo(() => variacion(ventas, inicioMes()), [ventas])
  const serie = useMemo(() => serieDiaria(ventas, 14), [ventas])

  const pendientes = useMemo(
    () => vigentes(ventas).filter((v) => v.estado !== 'entregada'),
    [ventas],
  )
  const ultimas = useMemo(() => vigentes(ventas).slice(0, 5), [ventas])

  const ventasHoy = desde(ventas, inicioDia()).length
  const saludo = new Date().getHours() < 12 ? 'Buenos días' : new Date().getHours() < 20 ? 'Buenas tardes' : 'Buenas noches'

  // Clientes a los que ya toca volver a ofrecer: cada vendedor ve los
  // suyos (los que él mismo atendió la última vez); el administrador
  // los ve todos.
  const seguimientos = useMemo(() => {
    const todos = seguimientosPendientes(datos.ventas, datos.clientes)
    return esAdmin ? todos : todos.filter((s) => s.usuarioId === usuario.id)
  }, [datos.ventas, datos.clientes, esAdmin, usuario.id])

  // Cobros vencidos: a diferencia del seguimiento, esto es del negocio
  // completo y le aparece a todos los vendedores por igual, no solo a
  // quien hizo la venta.
  const cobrosVencidos = useMemo(
    () =>
      datos.ventas
        .filter((v) => v.metodoPago === 'credito' && !v.pagado && v.estado !== 'anulada')
        .filter((v) => diasHasta(v.vencimientoPago) <= 0)
        .sort((a, b) => (a.vencimientoPago ?? '').localeCompare(b.vencimientoPago ?? '')),
    [datos.ventas],
  )

  return (
    <Pagina>
      {/* Botón de instalar: su propia fila arriba del todo, para no
          apretar el saludo en pantallas chicas. */}
      <div className="mb-3 flex justify-end anim-aparecer">
        <BotonInstalarApp />
      </div>

      {/* Saludo */}
      <div className="mb-6 anim-aparecer">
        <p className="text-sm first-letter:uppercase texto-tenue">
          {fechaLarga(new Date().toISOString())}
        </p>
        <h2 className="mt-1 text-[28px] leading-tight font-bold texto-base sm:text-4xl">
          {saludo}, {usuario.nombre.split(' ')[0]}.
        </h2>
        <p className="mt-1.5 text-[15px] texto-suave">
          {ventasHoy === 0
            ? 'Todavía no hay ventas registradas hoy.'
            : `${ventasHoy} ${ventasHoy === 1 ? 'venta registrada' : 'ventas registradas'} hoy por ${money(hoy.actual)}.`}
        </p>
      </div>

      {/* Cobros vencidos: le aparece a todos los vendedores, no
          desaparece hasta que alguien lo marque como cobrado. */}
      {cobrosVencidos.length > 0 && (
        <Tarjeta
          className="mb-5 p-4 sm:p-5"
          style={BORDE_ROJO_NEON}
        >
          <div className="mb-3 flex items-center gap-2.5">
            <span className="grid size-9 shrink-0 place-items-center rounded-xl bg-rojo-50 text-rojo-600 dark:bg-rojo-500/15 dark:text-rojo-300">
              <Wallet className="size-[18px]" />
            </span>
            <div>
              <h3 className="text-[15px] font-bold texto-base">Cobros pendientes</h3>
              <p className="text-[12px] texto-suave">
                {cobrosVencidos.length}{' '}
                {cobrosVencidos.length === 1 ? 'venta vencida sin pagar' : 'ventas vencidas sin pagar'}
              </p>
            </div>
          </div>
          <ul className="space-y-2.5">
            {cobrosVencidos.map((v) => {
              const cliente = datos.clientes.find((c) => c.id === v.clienteId)
              const telefono = cliente?.telefono?.replace(/[^\d+]/g, '')
              return (
                <li
                  key={v.id}
                  className="rounded-2xl superficie-hundida p-3.5"
                >
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div className="min-w-0">
                      <Link
                        to={`/ventas/${v.id}`}
                        className="block truncate text-[14px] font-bold texto-base hover:underline"
                      >
                        {v.clienteNombre}
                      </Link>
                      <p className="mt-0.5 text-[12px] text-rojo-500 font-semibold">
                        {v.folio} · venció {relativo(v.vencimientoPago)}
                      </p>
                    </div>
                    <p className="cifra shrink-0 text-[15px] font-bold texto-base">{money(v.total)}</p>
                  </div>
                  <div className="mt-3 flex flex-wrap gap-2">
                    <Boton
                      tamano="sm"
                      icono={<Wallet className="size-4" />}
                      onClick={async () => {
                        await marcarPagada(v.id, usuario)
                        avisar(`${v.folio} marcada como cobrada`)
                      }}
                    >
                      Ya fue cobrado
                    </Boton>
                    {telefono && (
                      <a
                        href={`https://wa.me/${telefono.replace('+', '')}?text=${encodeURIComponent(
                          `Hola ${cliente?.nombre}, te escribimos de NovaBerry por el pago de ${v.folio} por ${money(v.total)}. ¡Gracias!`,
                        )}`}
                        target="_blank"
                        rel="noreferrer"
                      >
                        <Boton
                          tamano="sm"
                          variante="secundario"
                          icono={<MessageCircle className="size-4" />}
                          style={ESTILO_WHATSAPP}
                        >
                          WhatsApp
                        </Boton>
                      </a>
                    )}
                    {telefono && (
                      <a href={`tel:${telefono}`}>
                        <Boton tamano="sm" variante="secundario" icono={<Phone className="size-4" />}>
                          Llamar
                        </Boton>
                      </a>
                    )}
                  </div>
                </li>
              )
            })}
          </ul>
        </Tarjeta>
      )}

      {/* Seguimiento pendiente: clientes que ya deberían haber vuelto a
          pedir según su frecuencia habitual. */}
      {seguimientos.length > 0 && (
        <Tarjeta className="mb-5 p-4 sm:p-5" style={BORDE_ORO_NEON}>
          <div className="mb-3 flex items-center gap-2.5">
            <span className="grid size-9 shrink-0 place-items-center rounded-xl bg-[#e8b400]/10 text-[#a87c00] dark:text-[#f0c94d]">
              <Users className="size-[18px]" />
            </span>
            <div>
              <h3 className="text-[15px] font-bold texto-base">Volver a ofrecer</h3>
              <p className="text-[12px] texto-suave">
                {seguimientos.length}{' '}
                {seguimientos.length === 1
                  ? 'cliente ya debería haber pedido de nuevo'
                  : 'clientes ya deberían haber pedido de nuevo'}
              </p>
            </div>
          </div>
          <ul className="space-y-2">
            {seguimientos.map((s) => (
              <li key={s.clienteId}>
                <Link
                  to={`/clientes/${s.clienteId}`}
                  className="flex items-center justify-between gap-3 rounded-2xl superficie-hundida p-3.5 transition-colors hover:opacity-80"
                >
                  <div className="min-w-0">
                    <p className="truncate text-[14px] font-bold texto-base">{s.clienteNombre}</p>
                    <p className="mt-0.5 text-[12px] texto-suave">
                      Pide cada {s.frecuenciaPedidoDias} días · última venta {fecha(s.ultimaVenta)}
                    </p>
                  </div>
                  <span className="shrink-0 rounded-full bg-[#e8b400]/15 px-2.5 py-1 text-[11px] font-bold text-[#a87c00] dark:text-[#f0c94d]">
                    {s.diasDesde} días
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        </Tarjeta>
      )}

      {/* Tarjetas principales */}
      {esAdmin ? (
        <div className="mb-6 grid grid-cols-2 gap-3 escalonar lg:grid-cols-3">
          <TarjetaCifra
            etiqueta="Ventas del día"
            valor={money(hoy.actual)}
            delta={hoy.delta}
            pie={`${ventasHoy} ${ventasHoy === 1 ? 'venta' : 'ventas'}`}
            icono={Receipt}
            tono="bosque"
            onClick={() => navegar('/ventas')}
          />
          <TarjetaCifra
            etiqueta="Ventas de la semana"
            valor={money(semana.actual)}
            delta={semana.delta}
            pie="vs. semana anterior"
            icono={ArrowUpRight}
            tono="lima"
            onClick={() => navegar('/ventas')}
          />
          <TarjetaCifra
            etiqueta="Ventas del mes"
            valor={money(mes.actual)}
            delta={mes.delta}
            pie="vs. mes anterior"
            icono={ArrowUpRight}
            tono="magenta"
            onClick={() => navegar('/ventas')}
          />
          <TarjetaCifra
            etiqueta="Stock disponible"
            valor={num(inv.unidades)}
            pie={`${inv.productos} productos · ${moneyCorto(inv.valorVenta)}`}
            icono={Box}
            tono="tinta"
            onClick={() => navegar('/inventario')}
          />
          <TarjetaCifra
            etiqueta="Clientes"
            valor={num(datos.clientes.length)}
            pie={`${datos.clientes.filter((c) => c.tipo === 'empresa').length} empresas`}
            icono={Users}
            tono="bosque"
            onClick={() => navegar('/clientes')}
          />
          <TarjetaCifra
            etiqueta="Pedidos pendientes"
            valor={num(pendientes.length)}
            pie={pendientes.length ? moneyCorto(suma(pendientes)) : 'Todo entregado'}
            icono={Truck}
            tono="rojo"
            onClick={() => navegar('/pedidos')}
          />
        </div>
      ) : (
        <div className="mb-6 escalonar">
          <Tarjeta className="mb-3 divide-y borde-suave overflow-hidden p-0">
            <FilaVenta
              etiqueta="Ventas del día"
              valor={money(hoy.actual)}
              delta={hoy.delta}
              pie={`${ventasHoy} ${ventasHoy === 1 ? 'venta' : 'ventas'}`}
              icono={Receipt}
              tono="bosque"
              onClick={() => navegar('/ventas')}
            />
            <FilaVenta
              etiqueta="Ventas de la semana"
              valor={money(semana.actual)}
              delta={semana.delta}
              pie="vs. semana anterior"
              icono={ArrowUpRight}
              tono="lima"
              onClick={() => navegar('/ventas')}
            />
            <FilaVenta
              etiqueta="Ventas del mes"
              valor={money(mes.actual)}
              delta={mes.delta}
              pie="vs. mes anterior"
              icono={ArrowUpRight}
              tono="magenta"
              onClick={() => navegar('/ventas')}
            />
          </Tarjeta>
          <TarjetaPedidos
            valor={num(pendientes.length)}
            pie={pendientes.length ? moneyCorto(suma(pendientes)) : 'Todo entregado'}
            onClick={() => navegar('/pedidos')}
          />
        </div>
      )}

      {/* Tendencia */}
      <Tarjeta className="p-5">
        <div className="mb-4 flex items-end justify-between gap-3">
          <div>
            <h3 className="text-base font-bold texto-base">Últimos 14 días</h3>
            <p className="mt-0.5 text-sm texto-suave">
              {money(serie.reduce((s, d) => s + d.total, 0))} en total
            </p>
          </div>
          <Link
            to={esAdmin ? '/estadisticas' : '/ventas'}
            className="text-[13px] font-semibold text-bosque-500 hover:underline dark:text-bosque-200"
          >
            Ver detalle
          </Link>
        </div>

        <div className="h-44 -mx-2">
          <Suspense fallback={<div className="size-full rounded-2xl superficie-hundida anim-pulso" />}>
            <GraficoVentas datos={serie} />
          </Suspense>
        </div>
      </Tarjeta>

      {/* Últimas ventas */}
      <Tarjeta className="mt-4 overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4">
          <h3 className="text-base font-bold texto-base">Últimas ventas</h3>
          <Link
            to="/ventas"
            className="text-[13px] font-semibold text-bosque-500 hover:underline dark:text-bosque-200"
          >
            Ver todas
          </Link>
        </div>

        {ultimas.length === 0 ? (
          <p className="px-5 pb-8 text-center text-sm texto-tenue">
            Aún no registras ventas. El botón verde de abajo abre una nueva.
          </p>
        ) : (
          <ul className="divide-y borde-suave">
            {ultimas.map((v) => (
              <li key={v.id}>
                <Link
                  to={`/ventas/${v.id}`}
                  className="flex items-center gap-3 px-5 py-3.5 transition-colors hover:superficie-hundida"
                >
                  <Avatar nombre={v.clienteNombre} tamano="sm" />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-[15px] font-semibold texto-base">{v.clienteNombre}</p>
                    <p className="truncate text-[13px] texto-suave">
                      <span className="codigo">{v.folio}</span> · {hora(v.fecha)} ·{' '}
                      {v.lineas.length} {v.lineas.length === 1 ? 'producto' : 'productos'}
                    </p>
                  </div>
                  <p className="cifra shrink-0 text-[15px] font-bold texto-base">{money(v.total)}</p>
                  <ChevronRight className="size-4 shrink-0 texto-tenue" />
                </Link>
              </li>
            ))}
          </ul>
        )}
      </Tarjeta>
    </Pagina>
  )
}

const TONOS = {
  bosque: 'bg-bosque-50 text-bosque-600 dark:bg-bosque-500/20 dark:text-bosque-200',
  lima: 'bg-lima-50 text-lima-700 dark:bg-lima-500/20 dark:text-lima-200',
  magenta: 'bg-magenta-50 text-magenta-600 dark:bg-magenta-500/20 dark:text-magenta-200',
  tinta: 'bg-tinta-50 text-tinta-600 dark:bg-tinta-500/20 dark:text-tinta-200',
  rojo: 'bg-rojo-50 text-rojo-600 dark:bg-rojo-500/20 dark:text-rojo-200',
}

/** Fila dentro de la tarjeta de ventas del vendedor: día, semana y mes
 *  juntos en un solo bloque prolijo, en vez de tres tarjetas sueltas. */
function FilaVenta({
  etiqueta,
  valor,
  pie,
  delta,
  icono: Icono,
  tono,
  onClick,
}: {
  etiqueta: string
  valor: string
  pie?: string
  delta?: number
  icono: typeof Box
  tono: keyof typeof TONOS
  onClick?: () => void
}) {
  const sube = (delta ?? 0) >= 0
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex w-full items-center gap-3 px-4 py-3.5 text-left transition-colors hover:superficie-hundida sm:px-5"
    >
      <span className={cn('grid size-9 shrink-0 place-items-center rounded-xl', TONOS[tono])}>
        <Icono className="size-[18px]" strokeWidth={2.4} />
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-[13px] font-semibold texto-suave">{etiqueta}</p>
        {pie && <p className="truncate text-[12px] texto-tenue">{pie}</p>}
      </div>
      <div className="flex shrink-0 flex-col items-end gap-1">
        <p className="cifra font-display text-[17px] leading-tight font-bold texto-base">{valor}</p>
        {delta !== undefined && (
          <span
            className={cn(
              'flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[10px] font-bold',
              sube
                ? 'bg-lima-50 text-lima-700 dark:bg-lima-500/15 dark:text-lima-300'
                : 'bg-rojo-50 text-rojo-600 dark:bg-rojo-500/15 dark:text-rojo-300',
            )}
          >
            {sube ? <ArrowUpRight className="size-2.5" /> : <ArrowDownRight className="size-2.5" />}
            {pct(Math.abs(delta))}
          </span>
        )}
      </div>
    </button>
  )
}

/** Tarjeta de "Pedidos pendientes" del vendedor: el camión va al lado
 *  del título, y el número grande queda debajo —así se lee de un
 *  vistazo, sin el ícono separado arriba como en TarjetaCifra. */
function TarjetaPedidos({
  valor,
  pie,
  onClick,
}: {
  valor: string
  pie?: string
  onClick?: () => void
}) {
  return (
    <Tarjeta onClick={onClick} className="p-4 sm:p-5">
      <div className="flex items-center justify-between gap-2.5">
        <div className="flex min-w-0 items-center gap-2.5">
          <span className={cn('grid size-9 shrink-0 place-items-center rounded-xl', TONOS.rojo)}>
            <Truck className="size-[18px]" strokeWidth={2.4} />
          </span>
          <div className="min-w-0">
            <p className="truncate text-[13px] font-semibold texto-suave">Pedidos pendientes</p>
            {pie && <p className="truncate text-[12px] texto-tenue">{pie}</p>}
          </div>
        </div>
        <p className="cifra shrink-0 font-display text-[26px] leading-tight font-bold texto-base sm:text-3xl">
          {valor}
        </p>
      </div>
    </Tarjeta>
  )
}

function TarjetaCifra({
  etiqueta,
  valor,
  pie,
  delta,
  icono: Icono,
  tono,
  onClick,
}: {
  etiqueta: string
  valor: string
  pie?: string
  delta?: number
  icono: typeof Box
  tono: keyof typeof TONOS
  onClick?: () => void
}) {
  const sube = (delta ?? 0) >= 0
  return (
    <Tarjeta onClick={onClick} className="p-4 sm:p-5">
      <div className="mb-3 flex items-start justify-between gap-2">
        <span className={cn('grid size-9 place-items-center rounded-xl', TONOS[tono])}>
          <Icono className="size-[18px]" strokeWidth={2.4} />
        </span>
        {delta !== undefined && (
          <span
            className={cn(
              'flex items-center gap-0.5 rounded-full px-2 py-1 text-[11px] font-bold',
              sube
                ? 'bg-lima-50 text-lima-700 dark:bg-lima-500/15 dark:text-lima-300'
                : 'bg-rojo-50 text-rojo-600 dark:bg-rojo-500/15 dark:text-rojo-300',
            )}
          >
            {sube ? <ArrowUpRight className="size-3" /> : <ArrowDownRight className="size-3" />}
            {pct(Math.abs(delta))}
          </span>
        )}
      </div>
      <p className="text-[13px] font-semibold texto-suave">{etiqueta}</p>
      <p className="cifra mt-0.5 font-display text-[26px] leading-tight font-bold texto-base sm:text-3xl">
        {valor}
      </p>
      {pie && <p className="mt-1 truncate text-[12px] texto-tenue">{pie}</p>}
    </Tarjeta>
  )
}
